import {
  AlertTriangle,
  Check,
  CheckCheck,
  Clock3,
  Download,
  FileText,
  HelpCircle,
  ListChecks,
  MessageSquareText,
  PencilLine,
} from 'lucide-react'
import { Button } from '../../components/ui/button'
import { formatMessageTime } from '../../lib/format'
import { cn } from '../../lib/cn'
import type { Message } from '../../types'

const statusInfo = (message: Message) => {
  switch (message.status) {
    case 'queued':
      return { label: 'In invio', icon: Clock3 }
    case 'accepted':
    case 'sent':
      return { label: 'Inviato', icon: Check }
    case 'delivered':
      return { label: 'Consegnato', icon: CheckCheck }
    case 'read':
      return { label: 'Letto', icon: CheckCheck }
    case 'failed':
      return { label: 'Invio fallito', icon: AlertTriangle }
    case 'unknown':
      return { label: 'Esito da verificare', icon: HelpCircle }
    default:
      return { label: 'Ricevuto', icon: Check }
  }
}

function MessageContent({ message }: { message: Message }) {
  const mediaUrl = `/api/messages/${message.id}/media`
  if (message.type === 'notice') {
    return (
      <div className="flex items-start gap-2.5 rounded-xl border border-[var(--warning-border)] bg-[var(--warning-soft)] p-3 text-[var(--warning)]">
        <PencilLine className="mt-0.5 size-4.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide">Messaggio modificato</p>
          <p className="mt-1 text-sm leading-6 text-[var(--foreground)]">{message.text}</p>
        </div>
      </div>
    )
  }
  if (message.type === 'button') {
    return (
      <div className="flex items-start gap-2.5 rounded-xl bg-[var(--muted)] p-3">
        <ListChecks className="mt-0.5 size-4.5 shrink-0 text-[var(--primary)]" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
            Risposta rapida
          </p>
          <p className="mt-1 text-sm font-semibold">{message.text}</p>
        </div>
      </div>
    )
  }
  if (message.type === 'template') {
    return (
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--primary)]">
        <MessageSquareText className="size-4" /> Template WhatsApp
      </div>
    )
  }
  if ((message.type === 'image' || message.type === 'sticker') && message.media?.downloadReady) {
    return (
      <a
        href={mediaUrl}
        target="_blank"
        rel="noreferrer"
        className="block overflow-hidden rounded-xl"
      >
        <img
          src={mediaUrl}
          alt={message.text ? `Immagine: ${message.text}` : 'Immagine ricevuta'}
          className="max-h-[360px] w-full object-cover"
          loading="lazy"
        />
      </a>
    )
  }
  if (message.type === 'audio' && message.media?.downloadReady) {
    return <audio controls preload="metadata" src={mediaUrl} className="max-w-full" />
  }
  if (message.type === 'video' && message.media?.downloadReady) {
    return (
      <video
        controls
        preload="metadata"
        src={mediaUrl}
        className="max-h-[360px] max-w-full rounded-xl"
      />
    )
  }
  if (message.type === 'document' && message.media?.downloadReady) {
    return (
      <a
        href={mediaUrl}
        target="_blank"
        rel="noreferrer"
        className="flex min-w-0 items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)]/70 p-3 hover:bg-[var(--surface)]"
      >
        <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--muted)]">
          <FileText className="size-5" />
        </div>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {message.media.fileName ?? 'Documento'}
        </span>
        <Download className="size-4 shrink-0" />
      </a>
    )
  }
  if (message.media && !message.media.downloadReady) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-[var(--muted)] p-3 text-sm text-[var(--muted-foreground)]">
        <Clock3 className="size-4 animate-pulse" /> Download del media in corso
      </div>
    )
  }
  if (message.type === 'unsupported') {
    return (
      <div className="flex items-center gap-2 text-sm italic text-[var(--muted-foreground)]">
        <HelpCircle className="size-4" /> Tipo di messaggio non ancora supportato
      </div>
    )
  }
  return null
}

export function MessageBubble({ message, onRetry }: { message: Message; onRetry: () => void }) {
  const outgoing = message.direction === 'outgoing'
  const status = statusInfo(message)
  return (
    <article className={cn('flex w-full', outgoing ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[88%] rounded-2xl px-3.5 py-2.5 shadow-sm sm:max-w-[72%] lg:max-w-[68%]',
          outgoing
            ? 'rounded-br-md bg-[var(--primary-soft)] text-[var(--foreground)]'
            : 'rounded-bl-md border border-[var(--border)] bg-[var(--surface)]',
        )}
      >
        <MessageContent message={message} />
        {message.text && !['button', 'notice'].includes(message.type) && (
          <p className="whitespace-pre-wrap break-words text-[15px] leading-6">{message.text}</p>
        )}
        <footer className="mt-1.5 flex items-center justify-end gap-1.5 text-[11px] text-[var(--muted-foreground)]">
          {message.editedAt && <span title={formatMessageTime(message.editedAt)}>modificato</span>}
          <time>{formatMessageTime(message.occurredAt)}</time>
          {outgoing && (
            <span
              className={cn(
                'inline-flex items-center gap-1',
                message.status === 'read' && 'text-[var(--primary)]',
              )}
              title={status.label}
            >
              <status.icon className="size-3.5" />
              <span className="sr-only">{status.label}</span>
            </span>
          )}
        </footer>
        {message.reaction && (
          <div
            className="mt-1.5 ml-auto flex w-fit min-w-8 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface)] px-2 py-0.5 text-base shadow-sm"
            aria-label={`Reazione ricevuta: ${message.reaction.emoji}`}
            title={`Reazione aggiornata ${formatMessageTime(message.reaction.updatedAt)}`}
          >
            {message.reaction.emoji}
          </div>
        )}
        {message.errorMessage && (
          <div
            className="mt-2 flex items-start gap-2 rounded-xl border border-[var(--destructive-border)] bg-[var(--destructive-soft)] p-2.5 text-xs leading-5 text-[var(--destructive)]"
            role="status"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <p>
              <span className="font-semibold">
                Errore WhatsApp{message.errorCode ? ` ${message.errorCode}` : ''}
              </span>
              <br />
              {message.errorMessage}
            </p>
          </div>
        )}
        {message.status === 'failed' && (
          <Button variant="ghost" size="sm" className="mt-1 h-8 px-2 text-xs" onClick={onRetry}>
            Riprova invio
          </Button>
        )}
        {message.status === 'unknown' && (
          <p className="mt-2 text-xs leading-5 text-[var(--warning)]">
            Non ritentare automaticamente: l’esito deve essere verificato per evitare duplicati.
          </p>
        )}
      </div>
    </article>
  )
}
